import axios from 'axios';
import { resolveAppApiOrigin } from '../../utils/appApiOrigin';
const apiOrigin = resolveAppApiOrigin();

const client = axios.create({
  baseURL: apiOrigin || undefined,
  withCredentials: true,
  timeout: 45_000,
});

async function post(path, body = {}) {
  try {
    const res = await client.post(`/api${path}`, body);
    return res.data;
  } catch (err) {
    const data = err.response?.data || {};
    const message = data.message || err.message || 'Request failed';
    const error = new Error(message);
    error.status = err.response?.status;
    error.code = data.code;
    throw error;
  }
}

function downloadUrl(itemId, { publicToken, lockSessionToken } = {}) {
  const origin = apiOrigin || (typeof window !== 'undefined' ? window.location.origin : '');
  if (lockSessionToken) {
    return `${origin}/api/storagebox/lock/files/${encodeURIComponent(itemId)}/download`;
  }
  if (publicToken) {
    return `${origin}/api/storagebox/public/files/${encodeURIComponent(itemId)}/download?token=${encodeURIComponent(publicToken)}`;
  }
  return `${origin}/api/storagebox/files/${encodeURIComponent(itemId)}/download`;
}

function messageFromArrayBufferBody(data, fallback) {
  if (!(data instanceof ArrayBuffer) || !data.byteLength) return fallback;
  try {
    const parsed = JSON.parse(new TextDecoder().decode(data));
    if (parsed?.message) return parsed.message;
  } catch {
    /* response was not JSON */
  }
  return fallback;
}

async function fetchLockFileCiphertext(itemId, lockSessionToken) {
  try {
    const res = await client.get(`/api/storagebox/lock/files/${encodeURIComponent(itemId)}/download`, {
      headers: { 'x-sb-lock-session': lockSessionToken },
      responseType: 'arraybuffer',
      timeout: 120_000,
    });
    const encIv = res.headers['x-sb-enc-iv'] || res.headers['X-SB-Enc-Iv'];
    const encAuthTag = res.headers['x-sb-enc-tag'] || res.headers['X-SB-Enc-Tag'];
    const mimeType = res.headers['x-sb-plain-mime'] || res.headers['X-SB-Plain-Mime'];
    return {
      ciphertext: res.data,
      encIv,
      encAuthTag,
      mimeType,
    };
  } catch (err) {
    const fallback = err.message || 'Could not open file';
    const message = messageFromArrayBufferBody(err.response?.data, err.response?.data?.message || fallback);
    const error = new Error(message);
    error.status = err.response?.status;
    throw error;
  }
}

async function uploadLockFiles(parentId, files, lockSessionToken, { encMeta, onProgress } = {}) {
  const form = new FormData();
  if (parentId) form.append('parentId', parentId);
  form.append('clientEncrypted', 'true');
  form.append('encMeta', JSON.stringify(encMeta || []));
  files.forEach((file) => form.append('files', file));
  try {
    const res = await client.post('/api/storagebox/lock/files/upload', form, {
      timeout: 120_000,
      headers: { 'x-sb-lock-session': lockSessionToken },
      onUploadProgress: (evt) => {
        if (!onProgress || !evt.total) return;
        onProgress({
          phase: 'uploading',
          loaded: evt.loaded,
          total: evt.total,
          percent: Math.min(100, Math.round((evt.loaded / evt.total) * 100)),
        });
      },
    });
    return res.data;
  } catch (err) {
    const data = err.response?.data || {};
    throw new Error(data.message || err.message || 'Upload failed');
  }
}

function lockAuthHeaders(lockSessionToken) {
  return lockSessionToken ? { 'x-sb-lock-session': lockSessionToken } : {};
}

async function postWithLock(path, body, lockSessionToken) {
  try {
    const res = await client.post(`/api${path}`, body, {
      headers: lockAuthHeaders(lockSessionToken),
    });
    return res.data;
  } catch (err) {
    const data = err.response?.data || {};
    const error = new Error(data.message || err.message || 'Request failed');
    error.status = err.response?.status;
    error.code = data.code;
    throw error;
  }
}

async function uploadFiles(parentId, files, relativePaths, options = {}) {
  const { onProgress } = options;
  const form = new FormData();
  if (parentId) form.append('parentId', parentId);
  files.forEach((file) => form.append('files', file));
  const path = relativePaths?.length ? '/storagebox/files/upload-tree' : '/storagebox/files/upload';
  if (relativePaths?.length) {
    form.append('relativePaths', JSON.stringify(relativePaths));
  }
  try {
    const res = await client.post(`/api${path}`, form, {
      timeout: 120_000,
      onUploadProgress: (evt) => {
        if (!onProgress || !evt.total) return;
        onProgress({
          phase: 'uploading',
          loaded: evt.loaded,
          total: evt.total,
          percent: Math.min(100, Math.round((evt.loaded / evt.total) * 100)),
        });
      },
    });
    return res.data;
  } catch (err) {
    const data = err.response?.data || {};
    throw new Error(data.message || err.message || 'Upload failed');
  }
}

export const storageBoxApi = {
  summary: () => post('/storagebox/summary'),
  setViewMode: (viewMode) => post('/storagebox/preferences/view-mode', { viewMode }),
  setSortPreference: (sortField, sortDir) =>
    post('/storagebox/preferences/sort', { sortField, sortDir }),
  setSidebarWidth: (widthPx) => post('/storagebox/preferences/sidebar-width', { widthPx }),
  listItems: (parentId) => post('/storagebox/items/list', { parentId }),
  listTreeChildren: (parentId) => post('/storagebox/items/list-tree', { parentId }),
  searchItems: (q, limit, scope = 'drive') => post('/storagebox/items/search', { q, limit, scope }),
  lockSearchItems: (q, lockSessionToken, limit) =>
    postWithLock('/storagebox/lock/items/search', { q, limit }, lockSessionToken),
  createFolder: (parentId, name) => post('/storagebox/folders/create', { parentId, name }),
  renameItem: (itemId, name) => post('/storagebox/items/rename', { itemId, name }),
  moveItem: (itemId, parentId) => post('/storagebox/items/move', { itemId, parentId }),
  copyItem: (itemId, parentId) => post('/storagebox/items/copy', { itemId, parentId }),
  deleteItem: (itemId) => post('/storagebox/items/delete', { itemId }),
  restoreItem: (itemId) => post('/storagebox/items/restore', { itemId }),
  listBin: (scope = 'drive') => post('/storagebox/items/list-bin', { scope }),
  emptyBin: (scope = 'drive') => post('/storagebox/bin/empty', { scope }),
  listFavorites: (scope = 'drive') => post('/storagebox/items/list-favorites', { scope }),
  listLabels: (scope = 'drive') => post('/storagebox/labels/list', { scope }),
  listItemsByLabel: (tagLabel, scope = 'drive') => post('/storagebox/items/list-by-label', { tagLabel, scope }),
  listItemsByColorTag: (colorTag, scope = 'drive') =>
    post('/storagebox/items/list-by-color-tag', { colorTag, scope }),
  setItemMeta: (itemId, meta) => post('/storagebox/items/meta', { itemId, ...meta }),
  uploadFiles,
  searchUsers: (q) => post('/storagebox/users/search', { q }),
  createShare: (body) => post('/storagebox/share/create', body),
  revokeShare: (shareId) => post('/storagebox/share/revoke', { shareId }),
  dismissShareAsGrantee: (shareId) => post('/storagebox/share/dismiss-grantee', { shareId }),
  listSharedByMe: () => post('/storagebox/shared-by-me/list'),
  listShares: (itemId) => post('/storagebox/share/list', { itemId }),
  getItemDetails: (itemId) => post('/storagebox/items/details', { itemId }),
  previewText: (itemId) => post('/storagebox/files/preview-text', { itemId }),
  saveTextContent: (itemId, text) => post('/storagebox/files/save-text', { itemId, text }),
  lockStatus: () => post('/storagebox/lock/status'),
  lockSetup: (pin, confirmPin) => post('/storagebox/lock/setup', { pin, confirmPin }),
  lockUnlock: (pin) => post('/storagebox/lock/unlock', { pin }),
  lockVaultNow: () => post('/storagebox/lock/lock'),
  lockResumeOwner: () => post('/storagebox/lock/resume-owner'),
  lockUnlockShared: (shareId, tempPin) =>
    post('/storagebox/lock/unlock-shared', tempPin != null ? { shareId, tempPin } : { shareId }),
  lockRequestPinReset: () => post('/storagebox/lock/request-pin-reset'),
  lockCompletePinReset: (body) => post('/storagebox/lock/complete-pin-reset', body),
  lockChangePin: (body) => post('/storagebox/lock/change-pin', body),
  lockListItems: (parentId, lockSessionToken) =>
    postWithLock('/storagebox/lock/items/list', { parentId }, lockSessionToken),
  lockCreateFolder: (parentId, name, lockSessionToken) =>
    postWithLock('/storagebox/lock/folders/create', { parentId, name }, lockSessionToken),
  lockCreateShare: (body, lockSessionToken) =>
    postWithLock('/storagebox/lock/share/create', body, lockSessionToken),
  listSharedWithMe: () => post('/storagebox/shared-with-me/list'),
  lockPreviewTextEncrypted: (itemId, lockSessionToken) =>
    postWithLock('/storagebox/lock/files/preview-text', { itemId }, lockSessionToken),
  uploadLockFiles,
  fetchLockFileCiphertext,
  downloadUrl,
  publicResolveShare: (token) => post('/storagebox/public/share/resolve', { token }),
  publicListItems: (token, parentId) => post('/storagebox/public/share/list', { token, parentId }),
};

export function formatBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let u = 0;
  while (size >= 1024 && u < units.length - 1) {
    size /= 1024;
    u += 1;
  }
  return `${size.toFixed(u === 0 ? 0 : 1)} ${units[u]}`;
}

export function isPreviewableImage(mime, name) {
  const m = (mime || '').toLowerCase();
  if (m.startsWith('image/')) return true;
  return /\.(png|jpe?g|gif|webp|svg)$/i.test(name || '');
}

export function isPreviewableVideo(mime, name) {
  const m = (mime || '').toLowerCase();
  if (m.startsWith('video/')) return true;
  if (m.includes('quicktime')) return true;
  return /\.(mp4|m4v|webm|mov|qt)$/i.test(name || '');
}

export function isPreviewablePdf(mime, name) {
  return (mime || '').includes('pdf') || /\.pdf$/i.test(name || '');
}

export function isPreviewableText(mime, name) {
  const m = (mime || '').toLowerCase();
  if (m.startsWith('text/')) return true;
  return /\.(txt|md|json|csv|log|xml|ya?ml)$/i.test(name || '');
}

export { isPreviewableAudio } from '../../layout/ReusableComponent/audioPlayer/audioFormatUtils';

export {
  getOfficeDocumentKind,
  isPreviewableOfficeDocument,
  officeDocumentPreviewLabel,
} from '../../layout/ReusableComponent/MobileMediaViewer/officeDocumentPreview';
